import type {JSX, ReactElement, ReactNode} from 'react'
import React, {cloneElement, isValidElement} from 'react'
import {clsx} from 'clsx'
import {useWindowSize} from '@/utilities/hooks/use-window-size'

interface HeaderBlockCarouselSlideProps {
	className?: string
	button: ReactNode
	image: ReactElement<{
		className: string
	}> | null
	subheading: string
	heading: string
	url: string
	headingUrl: string
	isContentOnTopOfImage?: boolean
}

export const HeaderBlockCarouselSlide = ({
	className = '',
	button,
	image,
	subheading,
	heading,
	url,
	headingUrl,
	isContentOnTopOfImage,
}: HeaderBlockCarouselSlideProps): JSX.Element => {
	const size = useWindowSize()
	const isMobile = size.isMobile

	// Render heading as h1 for all slides
	const HeadingTag = 'h1'
	const renderHeading = (headingClassName: string): JSX.Element => (
		<HeadingTag className={headingClassName}>{heading}</HeadingTag>
	)

	// Render image with consistent styling
	const renderImage = (): JSX.Element | null =>
		isValidElement(image)
			? cloneElement(image, {
					className: 'w-full object-cover',
				})
			: null

	// Render image link wrapper
	const renderImageLink = (): JSX.Element => <a href={url}>{renderImage()}</a>

	if (!isContentOnTopOfImage) {
		return (
			<div className={clsx('relative', className)}>
				{renderImageLink()}
				<div className="bottom-0 w-full pl-6 pr-6">
					<div className="slide-text-block mx-auto max-w-screen-xl items-center justify-between p-4 md:flex">
						<div className="md:mr-1">
							<a href={headingUrl}>{renderHeading('mb-4')}</a>
							<a href={url}>
								<p>{subheading}</p>
							</a>
						</div>
						{Boolean(button) && button}
					</div>
				</div>
			</div>
		)
	}
	if (!isMobile) {
		return (
			<div className={clsx('relative', className)}>
				{subheading && heading ? (
					<>
						<div className="absolute h-full w-full overflow-hidden">
							<div className="container-wide flex h-full items-center">
								<div className="flex h-full w-full items-center">
									<div className="items-left max-w-2/3 w-1/2 flex-col justify-between rounded-lg bg-white p-2 md:flex md:max-h-full lg:w-1/3 lg:p-4">
										<div className="pb-2 md:mr-1 md:pb-4">
											<a href={headingUrl}>
												{renderHeading(
													'mb-2 text-xl leading-none text-black lg:mb-4 lg:text-2xl'
												)}
											</a>
											<a href={url}>
												<p className="text-sm text-black lg:text-base">
													{subheading}
												</p>
											</a>
										</div>
										{Boolean(button) && button}
									</div>
								</div>
							</div>
						</div>
						{renderImageLink()}
					</>
				) : (
					renderImageLink()
				)}
			</div>
		)
	}

	return (
		<div className={clsx('relative', className)}>
			{subheading && heading ? (
				<>
					{renderImageLink()}
					<div className="bottom-0 w-full pl-6 pr-6">
						<div className="slide-text-block mx-auto max-w-screen-xl items-center justify-between p-4 md:flex">
							<div className="md:mr-1">
								<a href={headingUrl}>{renderHeading('mb-4')}</a>
								<a href={url}>
									<p>{subheading}</p>
								</a>
							</div>
							{Boolean(button) && button}
						</div>
					</div>
				</>
			) : (
				renderImageLink()
			)}
		</div>
	)
}
